import { useEffect, useRef, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { selectLatestDownloadUrl, selectIsDownloading, selectDownloadingFileType, downloadUrlReceived } from 'src/infra/stores/reducer/sseSlice';
import { CircularProgress } from '@mui/material';

import { toast } from 'react-toastify';
import Snackbar from 'src/presentations/components/atom/snackbar/Snackbar';
import IconCheckCircle from 'src/presentations/components/atom/icons/CheckCircle';
import IconExclamationCircle from 'src/presentations/components/atom/icons/ExclamationCircle';
import CloseIcon from '@mui/icons-material/Close';

/**
 * Global component to listen for SSE download URL events and trigger file downloads
 * Mount this at the top level of the application
 */
export const GlobalDownloadListener = () => {
  const dispatch = useDispatch();
  const latestDownloadUrl = useSelector(selectLatestDownloadUrl);
  const isDownloading = useSelector(selectIsDownloading);
  const downloadingFileType = useSelector(selectDownloadingFileType);
  // Keep track of the last processed URL to avoid re-downloading on re-renders or mounting
  // Initialized to the current value so we don't auto-download on page refresh if state persists
  const processedUrlRef = useRef<string | null>(latestDownloadUrl);

  const [snackbarState, setSnackbarState] = useState<{
    open: boolean;
    type: 'success' | 'error' | 'downloading' | null;
    title: string;
    description?: string;
  }>({ open: false, type: null, title: '' });

  const handleCloseSnackbar = () => {
    if (snackbarState.type === 'downloading') return; // Cannot close while downloading? Optional choice.
    setSnackbarState((prev) => ({ ...prev, open: false }));
  };

  // Watch for downloading state
  useEffect(() => {
    if (isDownloading) {
      setSnackbarState({
        open: true,
        type: 'downloading',
        title: `${downloadingFileType === 'CSV' ? 'CSV' : 'Excel'} file downloading`,
      });
    }
  }, [isDownloading, downloadingFileType]);

  useEffect(() => {
    // Only proceed if we have a URL and it's different from the one we last processed
    // This prevents re-downloading the same file if the component re-renders
    if (latestDownloadUrl && latestDownloadUrl !== processedUrlRef.current) {
      processedUrlRef.current = latestDownloadUrl;

      // 1. Dismiss "Downloading" toast (legacy check, though we don't use it anymore)
      toast.dismiss('download-file-toast');

      // Dispatch downloadUrlReceived to reset isDownloading (handled in reducer? No, reducer handles it but we should check flow)
      // reducer `downloadUrlReceived` already sets `isDownloading = false`.
      // So `isDownloading` becomes false, `useEffect` above might trigger? No, only on true.
      // We just need to show Success now.

      // 2. Trigger download
      try {
        const link = document.createElement('a');
        link.href = latestDownloadUrl;
        link.setAttribute('download', 'download'); // The filename might be determined by Content-Disposition header
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        // 3. Show "Success" snackbar
        setSnackbarState({
          open: true,
          type: 'success',
          title: `${downloadingFileType === 'CSV' ? 'CSV' : 'Excel'} file downloaded`,
        });

        // Auto hide after 3 seconds
        setTimeout(() => {
          setSnackbarState((prev) => ({ ...prev, open: false }));
        }, 3000);
      } catch (error) {
        console.error('Download trigger failed', error);

        // Dimiss loading toast if it's still there
        toast.dismiss('download-file-toast');

        // Show error snackbar
        setSnackbarState({
          open: true,
          type: 'error',
          title: 'Download failed',
          description: 'Could not trigger the file download.',
        });
      }
    }
  }, [latestDownloadUrl, downloadingFileType]);

  return (
    <>
      {snackbarState.open && (
        <Snackbar
          icon={
            snackbarState.type === 'success' ? (
              <IconCheckCircle size={24} />
            ) : snackbarState.type === 'downloading' ? (
              <CircularProgress size={20} style={{ color: '#00A651' }} />
            ) : (
              <IconExclamationCircle size={24} />
            )
          }
          content={
            <div className='flex flex-col'>
              <span className='text-14 font-medium'>{snackbarState.title}</span>
              {snackbarState.description && <span className='text-12 text-neutral-40 mt-2'>{snackbarState.description}</span>}
            </div>
          }
          actions={
            snackbarState.type !== 'downloading' ? (
              <button onClick={handleCloseSnackbar} className='text-neutral-40 hover:text-white transition-colors'>
                <CloseIcon sx={{ width: 20, height: 20 }} />
              </button>
            ) : undefined
          }
          autoSidebarPlacement
          zIndex={9999}
        />
      )}
    </>
  );
};
